"use client";

import React, { useMemo } from "react";
import { Button, Select, SelectItem } from "@heroui/react";
import { IconFilterSearch } from "@tabler/icons-react";

import NameFilter from "@/components/Filters/NameFilter";
import GenericFilter from "@/components/Filters/GenericFilter";
import ShowAAsFilter from "@/components/Filters/ShowAAsFilter";
import type { CardItem } from "@/interfaces/CardItem";
import {
  DeckBuilderFilterSpec,
  FilterState,
  SelectFilterSpec,
  optionsFor,
  searchSelectItems,
} from "@/components/DeckBuilder/filters/spec";

/**
 * A deck builder filter bar rendered from a game's spec.
 *
 * This replaces the per-game OPFilters / UAFilters / RBFilters components for
 * any game that declares a spec: the controls, their order and their options all
 * come from the declaration, so a new game needs no component of its own.
 */

/**
 * A dropdown over one card field. Kept here rather than reusing the bespoke
 * ColorFilter/CostFilter components because those hardcode one game's options;
 * a spec supplies its own, including the colour swatches.
 */
function SelectFilter({
  spec,
  cards,
  selected,
  setSelected,
}: {
  spec: SelectFilterSpec;
  cards: CardItem[];
  selected: Set<string>;
  setSelected: (value: Set<string>) => void;
}) {
  // Derived options depend on the pool, which does not change while the builder
  // is open, so this is computed once per spec rather than per keystroke.
  const options = useMemo(() => optionsFor(spec, cards), [spec, cards]);

  if (options.length === 0) return null;

  const hasSwatches = options.some((option) => option.swatch);

  return (
    <Select
      size="sm"
      aria-label={spec.label}
      placeholder={spec.label}
      selectedKeys={Array.from(selected ?? new Set<string>())}
      selectionMode={spec.selectionMode ?? "multiple"}
      className={`${spec.width ?? "w-[130px]"} min-h-0`}
      classNames={{ trigger: "min-h-0 h-[32px]" }}
      onSelectionChange={(keys) =>
        setSelected(new Set(Array.from(keys).map(String)))
      }
    >
      {options.map((option) => (
        <SelectItem
          key={option.key}
          startContent={
            hasSwatches && option.swatch ? (
              <div className={`${option.swatch} h-2 w-2 rounded-full`} />
            ) : undefined
          }
        >
          {option.label}
        </SelectItem>
      ))}
    </Select>
  );
}

export default function SpecFilters({
  spec,
  cards,
  filters,
  setFilter,
  searchDisplay,
  setSearchDisplay,
  clearAllFilters,
}: {
  spec: DeckBuilderFilterSpec;
  /** The unfiltered pool, for the filters whose options are derived from it. */
  cards: CardItem[];
  filters: FilterState;
  setFilter: (key: string, value: any) => void;
  searchDisplay: string;
  setSearchDisplay: (value: string) => void;
  clearAllFilters: () => void;
}) {
  return (
    <div className="mt-2 mb-2 flex flex-wrap items-center gap-2">
      {spec.map((field) => {
        if (field.kind === "search") {
          return (
            <div key="search" className="flex flex-row">
              <GenericFilter
                classNames="w-[114px]"
                triggerClassNames="rounded-l-md rounded-r-none"
                selected={filters.searchSelect ?? new Set(["all"])}
                setSelected={(val: any) => setFilter("searchSelect", val)}
                startContent={<IconFilterSearch height={30} width={30} />}
                label="Search Filter"
                selectionMode="single"
                color="primary"
                data={searchSelectItems(field)}
              />
              <NameFilter
                selected={searchDisplay}
                setSelected={setSearchDisplay}
                placeholder="Search"
                rounded="rounded-l-none"
              />
            </div>
          );
        }

        if (field.kind === "toggle") {
          return (
            <ShowAAsFilter
              key={field.key}
              selected={Boolean(filters[field.key])}
              setSelected={(val: any) => setFilter(field.key, val)}
            />
          );
        }

        return (
          <SelectFilter
            key={field.key}
            spec={field}
            cards={cards}
            selected={filters[field.key]}
            setSelected={(val) => setFilter(field.key, val)}
          />
        );
      })}

      <Button onPress={clearAllFilters} radius="sm" className="h-[30px]">
        Clear
      </Button>
    </div>
  );
}
